'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import {
  useSaveTomVendingVendor,
  useTomVendingVendorsList,
  type TvVendor,
} from '@/hooks/useTomVendingPurchasing';
import { useNotification } from '@/contexts/NotificationContext';

const vendorFieldGroups = [
  {
    groupName: 'Vendor',
    fields: [
      { label: 'Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
      { label: 'Code', name: 'code', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Active', name: 'active', type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 6 },
      { label: 'Contact email', name: 'contactEmail', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      { label: 'Contact phone', name: 'contactPhone', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
    ],
  },
];

const TomsVendingVendorsPage: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const saveMutation = useSaveTomVendingVendor();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TvVendor | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState([] as { id: string; desc: boolean }[]);

  const { data: list = [], isLoading, refetch } = useTomVendingVendorsList(search, showInactive);

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      (v) =>
        (v.name || '').toLowerCase().includes(term) ||
        (v.code || '').toLowerCase().includes(term) ||
        (v.contactEmail || '').toLowerCase().includes(term) ||
        (v.contactPhone || '').toLowerCase().includes(term),
    );
  }, [list, search]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: TvVendor) => {
    setEditing(row);
    setModalOpen(true);
  }, []);

  const handleDeactivate = useCallback(
    async (row: TvVendor) => {
      if (!window.confirm(`Deactivate vendor "${row.name}"?`)) return;
      try {
        await saveMutation.mutateAsync({ id: row.id, name: row.name, active: false });
        showSuccess('Vendor deactivated');
        await refetch();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to deactivate');
      }
    },
    [refetch, saveMutation, showError, showSuccess],
  );

  const handleReactivate = useCallback(
    async (row: TvVendor) => {
      try {
        await saveMutation.mutateAsync({ id: row.id, name: row.name, active: true });
        showSuccess('Vendor reactivated');
        await refetch();
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to reactivate');
      }
    },
    [refetch, saveMutation, showError, showSuccess],
  );

  const columns = useMemo<MRT_ColumnDef<TvVendor>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', size: 70 },
      { accessorKey: 'name', header: 'Name', size: 200 },
      { accessorKey: 'code', header: 'Code', size: 100, Cell: ({ cell }) => cell.getValue<string>() || '—' },
      { accessorKey: 'contactEmail', header: 'Email', size: 180, Cell: ({ cell }) => cell.getValue<string>() || '—' },
      { accessorKey: 'contactPhone', header: 'Phone', size: 130, Cell: ({ cell }) => cell.getValue<string>() || '—' },
      {
        accessorKey: 'active',
        header: 'Status',
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label={row.original.active === false ? 'Inactive' : 'Active'}
            size="small"
            color={row.original.active === false ? 'default' : 'success'}
            variant="outlined"
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        Cell: ({ row }) => {
          const v = row.original;
          const isActive = v.active !== false;
          return (
            <GenericActionPicker
              row={v}
              actions={[
                {
                  icon: GENERIC_ICON_NAME.EDIT,
                  label: 'Edit',
                  onClick: (r) => openEdit(r),
                },
                ...(isActive
                  ? [
                      {
                        icon: GENERIC_ICON_NAME.BLOCK,
                        label: 'Deactivate',
                        onClick: (r: TvVendor) => void handleDeactivate(r),
                      },
                    ]
                  : [
                      {
                        icon: GENERIC_ICON_NAME.CHECK_CIRCLE,
                        label: 'Reactivate',
                        onClick: (r: TvVendor) => void handleReactivate(r),
                      },
                    ]),
              ]}
            />
          );
        },
      },
    ],
    [handleDeactivate, handleReactivate, openEdit],
  );

  const modalInitialValues = editing
    ? {
        name: editing.name ?? '',
        code: editing.code ?? '',
        contactEmail: editing.contactEmail ?? '',
        contactPhone: editing.contactPhone ?? '',
        active: editing.active !== false,
      }
    : {
        name: '',
        code: '',
        contactEmail: '',
        contactPhone: '',
        active: true,
      };

  return (
    <>
      <GenericFormModal
        visible={modalOpen}
        onDismiss={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={async (values: Record<string, unknown>) => {
          const name = String(values.name ?? '').trim();
          if (!name) {
            showError('Name is required');
            return;
          }
          try {
            await saveMutation.mutateAsync({
              id: editing?.id,
              name,
              code: String(values.code ?? '').trim() || null,
              contactEmail: String(values.contactEmail ?? '').trim() || null,
              contactPhone: String(values.contactPhone ?? '').trim() || null,
              active: values.active !== false,
            });
            showSuccess(editing ? 'Vendor updated' : 'Vendor created');
            setModalOpen(false);
            setEditing(null);
            await refetch();
          } catch (e: unknown) {
            showError(e instanceof Error ? e.message : 'Failed to save vendor');
          }
        }}
        initialValues={modalInitialValues}
        fieldGroups={vendorFieldGroups}
        title={editing ? 'Edit vendor' : 'Add vendor'}
      />

      <TitleBar
        breadcrumbs={[
          { label: 'Home', href: '/app/dashboard' },
          { label: 'Vendors' },
        ]}
        title="Vendors"
        rightContent={
          <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none' }}>
            + Add vendor
          </Button>
        }
      />

      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            placeholder="Search vendors…"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 300 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show inactive"
          />
        </Box>

        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableGlobalFilter={false}
          manualPagination={false}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={filteredData.length}
          state={{ isLoading, pagination, sorting }}
          initialState={{ density: 'compact', sorting: [{ id: 'name', desc: false }] }}
        />
      </Box>
    </>
  );
};

export default TomsVendingVendorsPage;
